"use client";

import {
	IconEdit,
	IconLoader,
	IconPlus,
	IconTrash,
	IconTruck,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminShippingMethods } from "@/features/shipping-methods/use-admin-shipping-methods";

export const Route = createFileRoute(
	"/$store/_layout/admin_/shipping-methods",
)({
	component: AdminShippingMethodsPage,
});

function AdminShippingMethodsPage() {
	const {
		methods,
		isLoading,
		isCreating,
		isUpdating,
		isDeleting,
		dialogOpen,
		setDialogOpen,
		editingMethodId,
		formName,
		setFormName,
		formCode,
		setFormCode,
		formMinDays,
		setFormMinDays,
		formMaxDays,
		setFormMaxDays,
		openCreateDialog,
		openEditDialog,
		handleSubmitMethod,
		handleRemoveMethod,
	} = useAdminShippingMethods();

	const isSubmitting = isCreating || isUpdating;
	const idName = React.useId();
	const idCode = React.useId();
	const idMinDays = React.useId();
	const idMaxDays = React.useId();

	if (isLoading) {
		return (
			<div className="flex h-64 items-center justify-center">
				<IconLoader className="size-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-6 px-4 py-6 lg:px-6">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h1 className="text-2xl font-semibold text-foreground">
						Métodos de envio (Admin)
					</h1>
					<p className="text-sm text-muted-foreground">
						Cadastre os métodos de envio disponíveis para as lojas
					</p>
				</div>
				<Button onClick={openCreateDialog}>
					<IconPlus className="mr-1.5 size-4" />
					Novo método
				</Button>
			</div>

			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{editingMethodId
								? "Editar método de envio"
								: "Cadastrar método de envio"}
						</DialogTitle>
						<DialogDescription>
							Os métodos cadastrados aqui ficarão disponíveis para os donos de
							loja em Configurações → Métodos de envio.
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label htmlFor={idName}>Nome</Label>
							<Input
								id={idName}
								placeholder="Ex: PAC, SEDEX, Motoboy"
								value={formName}
								onChange={(e) => setFormName(e.target.value)}
							/>
						</div>
						<div className="grid gap-2">
							<Label htmlFor={idCode}>Código</Label>
							<Input
								id={idCode}
								placeholder="Ex: pac, sedex, motoboy"
								value={formCode}
								onChange={(e) => setFormCode(e.target.value)}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="grid gap-2">
								<Label htmlFor={idMinDays}>Prazo mínimo (dias)</Label>
								<Input
									id={idMinDays}
									type="number"
									min={1}
									placeholder="Ex: 3"
									value={formMinDays}
									onChange={(e) => setFormMinDays(e.target.value)}
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor={idMaxDays}>Prazo máximo (dias)</Label>
								<Input
									id={idMaxDays}
									type="number"
									min={1}
									placeholder="Ex: 7"
									value={formMaxDays}
									onChange={(e) => setFormMaxDays(e.target.value)}
								/>
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDialogOpen(false)}>
							Cancelar
						</Button>
						<Button
							onClick={handleSubmitMethod}
							disabled={!formName.trim() || isSubmitting}
						>
							{isSubmitting ? (
								<>
									<IconLoader className="mr-1.5 size-4 animate-spin" />
									{editingMethodId ? "Salvando..." : "Cadastrando..."}
								</>
							) : editingMethodId ? (
								"Salvar alterações"
							) : (
								"Cadastrar"
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<IconTruck className="size-5" />
						Métodos cadastrados
					</CardTitle>
					<CardDescription>
						Lista de métodos de envio que os owners podem ativar em suas lojas
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-3">
						{methods.map((method) => (
							<div
								key={method.id}
								className="flex items-center justify-between rounded-lg border p-4"
							>
								<div>
									<div className="flex items-center gap-2">
										<span className="font-medium">{method.name}</span>
										{method.code && (
											<span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
												{method.code}
											</span>
										)}
									</div>
									<div className="mt-2 flex flex-wrap gap-2">
										{method.minDays && method.maxDays ? (
											<Badge variant="outline">
												{method.minDays}–{method.maxDays} dias úteis
											</Badge>
										) : method.minDays ? (
											<Badge variant="outline">
												a partir de {method.minDays} dias úteis
											</Badge>
										) : method.maxDays ? (
											<Badge variant="outline">
												até {method.maxDays} dias úteis
											</Badge>
										) : null}
									</div>
								</div>
								<div className="flex shrink-0 gap-1">
									<Button
										variant="ghost"
										size="icon"
										onClick={() => openEditDialog(method)}
										title="Editar"
									>
										<IconEdit className="size-4" />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										className="text-destructive hover:text-destructive"
										onClick={() => handleRemoveMethod(method.id)}
										disabled={isDeleting}
										title="Excluir"
									>
										<IconTrash className="size-4" />
									</Button>
								</div>
							</div>
						))}
					</div>
					{methods.length === 0 && (
						<p className="py-8 text-center text-sm text-muted-foreground">
							Nenhum método cadastrado. Clique em &quot;Novo método&quot; para
							adicionar.
						</p>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
